// React
import { useEffect, useState } from 'react';

// Material UI
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';

// Icons
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

// Redux
import { useSelector, useDispatch } from 'react-redux';
import {
  setStartDateTime,
  setEndDateTime,
  setInvalidDate,
  setInvalidTime,
} from '../../../../../../../store/suggestEventSlice';

// Utils
import { compareAsc, isSameDay } from 'date-fns';

// Styles
import styles from './RangeDateTimePicker.module.scss';

const RangeDateTimePicker = () => {
  const themeDate = createTheme({
    palette: { primary: { main: '#F17023' } },
    components: {
      MuiTextField: {
        defaultProps: { variant: 'standard' },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: { borderRadius: '10px' },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: { cursor: 'pointer', textAlign: 'center' },
        },
      },
      MuiInput: {
        defaultProps: { disableUnderline: true },
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            backgroundColor: '#E8EBED',
            padding: '6px 10px',
            borderRadius: '8px',
            border: '1px solid #E8EBED',
            '&:hover': { border: '1px solid #98A6AE' },
          },
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: { '&.Mui-selected': { color: 'white' } },
        },
      },
      MuiTypography: {
        styleOverrides: {
          body1: { fontSize: '16px' },
        },
      },
      MuiPickersCalendarHeader: {
        styleOverrides: {
          root: {
            marginTop: 0,
            marginBottom: '21px',
            justifyContent: 'center',
          },
          switchViewButton: { display: 'none' },
        },
      },
      MuiPickersArrowSwitcher: {
        styleOverrides: {
          button: { padding: 0, color: '#F17023' },
        },
      },
      MuiDayCalendar: {
        styleOverrides: {
          header: {
            justifyContent: 'center',
            '& > .MuiDayCalendar-weekDayLabel:first-of-type': { color: '#FAB97A' },
          },
          weekDayLabel: { color: '#C6CED2', fontSize: '14px' },
          weekContainer: {
            justifyContent: 'center',
            '& .MuiPickersDay-root': { fontWeight: 500 },
          },
        },
      },
    },
  });

  const themeTime = createTheme({
    palette: { primary: { main: '#F17023' } },
    components: {
      MuiTextField: {
        defaultProps: { variant: 'standard' },
      },
      MuiInputBase: {
        styleOverrides: {
          input: { cursor: 'pointer', textAlign: 'center' },
        },
      },
      MuiInput: {
        defaultProps: { disableUnderline: true },
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            width: '130px',
            maxWidth: '130px !important',
            backgroundColor: '#E8EBED',
            padding: '5px 8px',
            borderRadius: '8px',
            border: '1px solid #E8EBED',
            '&:hover': { border: '1px solid #98A6AE' },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: { padding: '2px' },
        },
      },
      MuiInputAdornment: {
        styleOverrides: {
          positionEnd: { marginLeft: '6px', marginRight: '6px' },
        },
      },
    },
  });

  const isAllDay = useSelector((state) => state.suggestEvent.isAllDay);
  const startDate = useSelector((state) => state.suggestEvent.startDateTime);
  const endDate = useSelector((state) => state.suggestEvent.endDateTime);
  const invalidDate = useSelector((state) => state.suggestEvent.invalidDate);
  const invalidTime = useSelector((state) => state.suggestEvent.invalidTime);
  const dispatch = useDispatch();

  const [startTime, setStartTime] = useState(new Date());
  const [endTime, setEndTime] = useState(new Date());
  // Controlled open state: the picker button is hidden (openPickerButton
  // display:none) so on desktop (Popper mode) there is no click target.
  // Wiring open+onClick lets the input field toggle the calendar dropdown.
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);

  useEffect(() => {
    setStartTime(new Date(startDate.getTime()));
  }, [startDate]);

  useEffect(() => {
    setEndTime(new Date(endDate.getTime()));
  }, [endDate]);

  const checkDatesValidity = (s, e) => {
    try {
      dispatch(setInvalidDate(false));
      dispatch(setInvalidTime(false));
      const sDate = new Date(s.getTime());
      const eDate = new Date(e.getTime());
      sDate.setHours(0, 0, 0, 0);
      eDate.setHours(0, 0, 0, 0);
      if (compareAsc(s, e) === 1) {
        if (compareAsc(sDate, eDate) === 0) {
          dispatch(setInvalidTime(true));
        } else {
          dispatch(setInvalidDate(true));
        }
      } else {
        dispatch(setInvalidDate(false));
        dispatch(setInvalidTime(false));
      }
    } catch { /* intentionally empty */ }
  };

  const handleStartChange = (value) => {
    if (value && !isNaN(value.getTime())) {
      checkDatesValidity(value, endDate);
      dispatch(setStartDateTime(new Date(value.getTime())));
    }
  };

  const handleEndChange = (value) => {
    if (value && !isNaN(value.getTime())) {
      checkDatesValidity(startDate, value);
      dispatch(setEndDateTime(new Date(value.getTime())));
    }
  };

  const highlightPicker = (id, isOpen) => {
    const picker = document.getElementById(id);
    if (isOpen) {
      picker.style.color = '#FB8D4B';
      // MUI Modal adds aria-hidden to <div#root> when the picker dialog opens.
      // Blur the input so no focused element sits under an aria-hidden ancestor.
      picker.blur();
    }
    else {picker.style.color = 'unset';}
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div className={styles.container}>
        <div className={styles.container__box}>
          <CalendarTodayOutlinedIcon htmlColor="#F17023"></CalendarTodayOutlinedIcon>
          <ThemeProvider theme={themeDate}>
            <DatePicker
              value={startDate}
              format="EEE dd MMM yyyy"
              onChange={(value) => handleStartChange(value)}
              minDate={new Date()}
              open={startDateOpen}
              onOpen={() => { setStartDateOpen(true); highlightPicker('start-date-picker', true); }}
              onClose={() => { setStartDateOpen(false); highlightPicker('start-date-picker', false); }}
              slotProps={{
                textField: {
                  id: 'start-date-picker',
                  onClick: () => setStartDateOpen(true),
                  style: { maxWidth: 170, ...(invalidDate ? { border: '2px solid red' } : {}) },
                },
                openPickerButton: { sx: { display: 'none' } },
              }}
            />
          </ThemeProvider>
          {!isAllDay && (
            <ThemeProvider theme={themeTime}>
              <div className={styles['vertical-line']}></div>
              <TimePicker
                ampm={false}
                value={startTime}
                onChange={(value) => handleStartChange(value)}
                onOpen={() => highlightPicker('start-time-picker', true)}
                onClose={() => highlightPicker('start-time-picker', false)}
                slots={{ openPickerIcon: AccessTimeIcon }}
                slotProps={{
                  textField: {
                    id: 'start-time-picker',
                    style: invalidTime ? { border: '2px solid red' } : {},
                  },
                }}
              />
            </ThemeProvider>
          )}
        </div>
        <div className={styles.container__box}>
          <CalendarTodayOutlinedIcon htmlColor="transparent"></CalendarTodayOutlinedIcon>
          <ThemeProvider theme={themeDate}>
            <DatePicker
              value={endDate}
              format="EEE dd MMM yyyy"
              onChange={(value) => handleEndChange(value)}
              minDate={startDate}
              open={endDateOpen}
              onOpen={() => { setEndDateOpen(true); highlightPicker('end-date-picker', true); }}
              onClose={() => { setEndDateOpen(false); highlightPicker('end-date-picker', false); }}
              slotProps={{
                textField: {
                  id: 'end-date-picker',
                  onClick: () => setEndDateOpen(true),
                  style: { maxWidth: 170, ...(invalidDate ? { border: '2px solid red' } : {}) },
                },
                openPickerButton: { sx: { display: 'none' } },
              }}
            />
          </ThemeProvider>
          {!isAllDay && (
            <ThemeProvider theme={themeTime}>
              <div className={styles['vertical-line']}></div>
              <TimePicker
                ampm={false}
                value={endTime}
                onChange={(value) => handleEndChange(value)}
                minTime={isSameDay(startDate, endDate) ? startDate : undefined}
                onOpen={() => highlightPicker('end-time-picker', true)}
                onClose={() => highlightPicker('end-time-picker', false)}
                slots={{ openPickerIcon: AccessTimeIcon }}
                slotProps={{
                  textField: {
                    id: 'end-time-picker',
                    style: invalidTime ? { border: '2px solid red' } : {},
                  },
                }}
              />
            </ThemeProvider>
          )}
        </div>
      </div>
    </LocalizationProvider>
  );
};

export default RangeDateTimePicker;
